<template>
    <div v-bind="$attrs" class="space-y-8 border-cyan-950/10 pb-6 sm:space-y-0 sm:divide-y sm:divide-cyan-950/10 sm:pb-0">
        <!-- Toggle -->
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
            <label for="periodic-audit-toggle" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5"> Audit Config </label>
            <div class="flex items-center space-x-3">
                <Switch
                    :model-value="modelValue.enabled"
                    @update:model-value="(v) => emit('update:modelValue', { ...modelValue, enabled: v })"
                    :class="[
                        modelValue.enabled ? 'bg-green-400' : 'bg-neutral-300',
                        'relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
                        loading ? 'disabled-toggle' : ''
                    ]"
                    :true-value="true"
                    :false-value="false"
                    :disabled="loading"
                    aria-label="periodic-audit-toggle">
                    <span
                        aria-hidden="true"
                        :class="[
                            modelValue.enabled ? 'translate-x-5' : 'translate-x-0',
                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out'
                        ]" />
                </Switch>
                <HalfCircleSpinner :loading="loading" />
            </div>
        </div>

        <!-- Audit Topics -->
        <div v-if="modelValue.enabled" class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 pl-10">
            <label class="text-sm font-semibold text-cyan-950 sm:pt-1.5">Audit Topics</label>
            <div class="sm:col-span-2 mt-2 sm:mt-0">
                <div class="flex w-full rounded-lg border border-dashed border-cyan-950/25 p-6">
                    <AppLoader :force-loading="loading" default-height="15vh" class="w-full">
                        <TreeView
                            v-if="auditTreeData.length && treeLevels.length && !loading"
                            class="w-full"
                            :tree-nodes="auditTreeData"
                            :tree-levels="treeLevels"
                            :allow-search="true"
                            :disable-create-and-edit="true"
                            :edit-mode="true" />
                        <div v-else class="w-full">
                            <div class="text-center w-full">
                                <div class="flex flex-col w-full max-w-sm px-4 mx-auto items-center justify-center">
                                    <div class="p-3 mx-auto text-cyan-950 bg-green-100 rounded-full">
                                        <QueueListIcon class="h-7 w-7" />
                                    </div>
                                    <p class="mt-2 text-cyan-950 text-xs">No audit topics available</p>
                                </div>
                            </div>
                        </div>
                    </AppLoader>
                </div>
            </div>
        </div>

        <!-- Timestamp Column -->
        <div v-if="modelValue.enabled" class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 pl-10">
            <label for="timestamp_column" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5"> Timestamp Column </label>
            <div class="flex items-center space-x-3">
                <input
                    id="timestamp_column"
                    :value="modelValue.timestampColumn"
                    @input="
                        (e) => {
                            emit('update:modelValue', { ...modelValue, timestampColumn: e.target.value });
                        }
                    "
                    type="text"
                    placeholder="e.g. updated_at"
                    class="block w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 sm:text-sm" />
            </div>
        </div>

        <!-- Interval Minutes -->
        <div v-if="modelValue.enabled" class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 pl-10">
            <label for="interval_minutes" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5"> Interval (minutes) </label>
            <div class="flex items-center space-x-3">
                <input
                    id="interval_minutes"
                    :value="modelValue.intervalMinutes"
                    @input="
                        (e) => {
                            emit('update:modelValue', { ...modelValue, intervalMinutes: parseInt(e.target.value, 10) || 0 });
                        }
                    "
                    type="number"
                    min="0"
                    class="block w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 sm:text-sm" />
            </div>
        </div>
    </div>
</template>

<script setup>
defineOptions({ inheritAttrs: false });
import { computed, watch, ref } from 'vue';
import _ from 'lodash';
import { Switch } from '@headlessui/vue';
import AppLoader from '@/components/AppLoader.vue';
import TreeView from '@/components/TreeView.vue';
import HalfCircleSpinner from '@/components/global/loader/HalfCircleSpinner.vue';
import { QueueListIcon } from '@heroicons/vue/24/solid';
import { markSelectedTopics } from '@/utils/utils';

const props = defineProps({
    modelValue: {
        type: Object,
        default: () => ({ enabled: false, topics: [], timestampColumn: '', intervalMinutes: 0 })
    },
    sourceTopics: { type: Array, default: () => [] },
    treeNodes: { type: Array, default: () => [] },
    treeLevels: { type: Array, default: () => [] },
    loading: Boolean
});

const emit = defineEmits(['update:modelValue']);

const formattedTopics = computed(() => {
    const { topics } = props.modelValue;

    const toReturn = _.map(topics, (topicId) => {
        const split = topicId.split('.');
        const head = split.shift();

        return {
            id: split.length ? head : '-',
            name: split.length ? split.join('.') : head,
            topic_id: topicId,
            topic: true
        };
    });

    return toReturn;
});

const auditTreeNodes = computed(() => {
    const cloned = _.cloneDeep(props.treeNodes);
    function annotate(nodes, prefix = '') {
        nodes.forEach((node) => {
            const fullName = prefix ? `${prefix}.${node.name}` : node.name;
            if (node.children && node.children.length) {
                annotate(node.children, fullName);
                // disable parent if all children are disabled
                node.disabled = node.children.every((child) => child.disabled);
            } else {
                node.disabled = !props.sourceTopics.includes(fullName);
            }
            // clear any selection on disabled nodes (including parents)
            if (node.disabled) {
                node.isSelected = false;
            }
        });
    }
    annotate(cloned);
    markSelectedTopics(cloned, formattedTopics.value);

    return cloned;
});

// Local reactive copy so TreeView can update selections whenever the annotated tree changes
const auditTreeData = ref([]);

watch(
    () => auditTreeNodes.value,
    (nodes) => {
        auditTreeData.value = _.cloneDeep(nodes);
    },
    { immediate: true, deep: true }
);

watch(
    () => auditTreeData.value,
    () => {
        //  auditTreeData.value = _.cloneDeep(nodes);
        onAuditSelectionChange();
    },
    { immediate: true, deep: true }
);

// Remove topics that are no longer in sourceTopics to keep audit tree in sync
watch(
    () => props.sourceTopics,
    (newSource) => {
        if (props.modelValue.enabled) {
            const filtered = props.modelValue.topics.filter((t) => newSource.includes(t));
            if (!_.isEqual(filtered, props.modelValue.topics)) {
                emit('update:modelValue', { ...props.modelValue, topics: filtered });
            }
        }
    },
    { deep: true }
);

// Emit new topics list when user toggles selection
function onAuditSelectionChange() {
    const newTopics = [];

    const currentData = _.cloneDeep(auditTreeData.value);

    collectTopics(currentData, '', newTopics);

    if (_.isEqual(newTopics, props.modelValue.topics) || (_.isEmpty(newTopics) && _.isEmpty(props.modelValue.topics))) {
        // No change in topics
        return;
    }

    emit('update:modelValue', { ...props.modelValue, topics: newTopics });
}

function collectTopics(nodes, prefix, out) {
    nodes.forEach((node) => {
        const fullName = prefix ? `${prefix}.${node.name}` : node.name;
        if ((!node.children || !node.children.length) && node.isSelected) {
            out.push(fullName);
        }
        if (node.children) {
            collectTopics(node.children, fullName, out);
        }
    });
}
</script>

<style scoped>
.disabled-toggle {
    cursor: not-allowed;
    opacity: 0.5;
}
</style>
